import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import type { ConversationEmber } from '../api';
import { EmberBody } from './EmberBody';
import { HandoffButton } from './HandoffButton';
import { Link } from './Link';
import './EmberReadPage.css';

/**
 * The Ember read in place, over the conversation it came from (2026-09-12,
 * §4.5): the same sheet as /me/embers/:id — cover, music, receipt, title, the
 * text, the door to the original conversation — opened from its card in the
 * thread, with one door back to the chat. What this page is for is reading;
 * the owner's management of the Ember (who has read it, the two switches,
 * delete) stays on /me/embers/:id, and its door stands in the bar beside
 * Send (2026-09-19 拍板; before that at the foot, as a second underlined
 * link under the transcript door). The bar is one family — the quiet
 * icon + text the owner's page toolbar already wears. The one action that
 * travels with the Ember wherever it opens is Send (§4.18) — the link is
 * minted when the sheet opens, so the page needs no hand-off state of its
 * own. A guest reads here too (the Ember lives in their browser; hand-off
 * is theirs as well); `note` is the thread's one line for them about
 * keeping it, under the sheet.
 */
export function EmberReadPage({ ember, owner, note, onBack, restored = false }: {
  ember: ConversationEmber;
  /** A signed-in owner: the transcript door and the settings door show. */
  owner: boolean;
  note?: ReactNode;
  onBack: () => void;
  /**
   * The sheet is here because the URL said so — a Back from the light's page
   * or the transcript, or a reload (2026-09-24). Then it does not rise: it
   * was already standing when the person left, and playing the entry again
   * shows them the thread for a beat and flies the paper back in, which reads
   * as "two pages back" when they pressed Back once.
   */
  restored?: boolean;
}): ReactElement {
  // The link the sheet minted, kept for the next open on this page.
  const [handoffPath, setHandoffPath] = useState<string | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onBack(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onBack]);
  return (
    <div className={`ember-read${restored ? ' is-restored' : ''}`} role="dialog" aria-modal="true" aria-label={ember.title || 'Your Ember'}>
      <div className="ember-read-bar">
        <button
          type="button"
          className="ember-read-back"
          data-log="talk_ember_open"
          data-log-state="close"
          data-log-ember-id={ember.id}
          onClick={onBack}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M15 18l-6-6 6-6" />
          </svg>
          <span>Back to chat</span>
        </button>
        <div className="ember-read-actions">
          <HandoffButton
            subject={{ kind: 'ember', id: ember.id }}
            title={ember.title || 'An Ember'}
            handoffPath={handoffPath}
            onHandoffPath={setHandoffPath}
            className="ember-read-send"
          />
          {owner && (
            <Link
              to={`/me/embers/${encodeURIComponent(ember.id)}#settings`}
              className="ember-read-settings"
              data-log="ember_settings_open"
              data-log-ember-id={ember.id}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
                <circle cx="16" cy="7" r="2.2" />
                <circle cx="10" cy="17" r="2.2" />
              </svg>
              <span>Settings</span>
            </Link>
          )}
        </div>
      </div>
      <div className="ember-read-body">
        <EmberBody
          ember={ember}
          transcriptHref={owner ? `/me/embers/${encodeURIComponent(ember.id)}/transcript` : null}
        />
        {!owner && note && <div className="ember-read-note">{note}</div>}
      </div>
    </div>
  );
}
